import { useEffect, useState } from 'react';
import { livetvApi } from '@/services/livetvApi';

// Module-level cache: the enabled flag is process-global and rarely changes,
// so we probe once per page load and share the result across every consumer
// (the nav link + the page). `undefined` = not yet probed.
let cached: boolean | undefined;
let inflight: Promise<boolean> | null = null;

async function probe(): Promise<boolean> {
  if (cached !== undefined) return cached;
  if (inflight) return inflight;
  inflight = livetvApi
    .enabled()
    .then((r) => {
      cached = r.enabled === true;
      return cached;
    })
    .catch(() => {
      // 404 (router unmounted) or any error → treated as disabled.
      cached = false;
      return false;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * Returns true once the backend confirms the Live TV section is enabled.
 * Only probes when `active` (i.e. the user has access) to avoid an
 * unauthenticated 401/404 on every guest page load.
 */
export function useLiveTvEnabled(active: boolean): boolean {
  const [enabled, setEnabled] = useState<boolean>(cached ?? false);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    void probe().then((v) => {
      if (!cancelled) setEnabled(v);
    });
    return () => {
      cancelled = true;
    };
  }, [active]);

  return enabled;
}

/** Test-only: reset the module cache between cases (forces a fresh probe). */
export function __resetLiveTvEnabledCache(): void {
  cached = undefined;
  inflight = null;
}

/** Test-only: seed the cache to a known value so the hook resolves without
 *  firing a probe fetch (keeps unrelated component tests from seeing the
 *  /api/livetv/enabled request in their fetch-call assertions). */
export function __primeLiveTvEnabledCache(value: boolean): void {
  cached = value;
  inflight = null;
}
